/**
 * /app/_widgets — internal preview route for the widget catalog.
 *
 * Renders every widget in lib/widgets/ against fixture data so a human
 * (or screenshot in a future visual-regression run) can verify them at
 * a glance. Phase 2 replaces this with Storybook; until then, the route
 * doubles as a live design reference for both tracks.
 *
 * Not linked from nav — accessed manually via /app/_widgets.
 */

import { useMemo, useState } from "react";
import { Cpu } from "lucide-react";
import { PageContainer } from "./_layout";
import { PageHeader } from "@/lib/ui/page-header";
import { Badge } from "@/lib/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/lib/ui/card";
import {
  AlarmList,
  EventFeed,
  KPICard,
  SparklineCell,
  StatusPill,
  Table,
  TimeseriesChart,
  type AlarmItem,
  type ColumnDef,
} from "@/lib/widgets";
import {
  FX_ALARMS,
  FX_BATH_TEMP,
  FX_DEVICES,
  FX_EVENTS,
  FX_KPI_SPARK,
  FX_POWER_KW,
  FX_SAND_MOISTURE,
  type DemoDeviceRow,
} from "@/lib/widgets/__fixtures";
import type { TimeseriesSeries } from "@/lib/widgets/types";
import { StatusGrid } from "@/lib/dashboard/widget-registry";
import type { LiveTagData, LiveTagDatum } from "@/lib/dashboard/use-live-tag-data";

/** A tag read in 15 minute buckets beside one read as raw changes at its own seconds (synthetic). */
const FX_BUCKETED: TimeseriesSeries = {
  id: "bucketed",
  label: "Pour weight (15 min)",
  points: Array.from({ length: 13 }, (_, i) => ({
    t: new Date(Date.UTC(2026, 9, 17, 19, 0) + i * 15 * 60_000).toISOString(),
    v: 410 + 8 * Math.sin(i / 2),
  })),
};
const FX_CHANGES: TimeseriesSeries = {
  id: "changes",
  label: "Ladle level (raw changes)",
  points: Array.from({ length: 13 }, (_, i) => ({
    t: new Date(Date.UTC(2026, 9, 17, 19, 4, 21) + i * 14 * 60_000 + (i % 3) * 37_000).toISOString(),
    v: i === 6 ? null : 395 + 10 * Math.cos(i / 3),
  })),
};

const liveDatum = (over: Partial<LiveTagDatum> & Pick<LiveTagDatum, "name">): LiveTagDatum => ({
  tagId: over.name,
  unit: "",
  dataKind: "boolean_state",
  latest: null,
  latestTime: null,
  series: [],
  ...over,
});
/** A status grid's live readings: two booleans, an enum, and a tag that has not reported (synthetic). */
const FX_STATUS_LIVE: LiveTagData = {
  "autopour.stopper_open": liveDatum({ name: "autopour.stopper_open", latest: 1, latestTime: "2026-10-17T19:19:21Z" }),
  "autopour.heater_on": liveDatum({ name: "autopour.heater_on", latest: 0, latestTime: "2026-10-17T18:02:00Z" }),
  "autopour.mode": liveDatum({
    name: "autopour.mode",
    dataKind: "categorical_enum",
    latestLabel: "AUTO",
    latestTime: "2026-10-17T19:00:05Z",
  }),
  "autopour.alarm_reset": liveDatum({ name: "autopour.alarm_reset" }),
};

export function WidgetsPreview() {
  const [alarms, setAlarms] = useState<AlarmItem[]>(FX_ALARMS);

  const deviceColumns = useMemo<ColumnDef<DemoDeviceRow>[]>(
    () => [
      {
        id: "name",
        header: "Name",
        accessor: (d) => d.name,
        cell: (d) => (
          <div className="flex flex-col">
            <span className="font-medium text-on-surface">{d.name}</span>
            <span className="text-[11px] text-on-surface-muted">{d.site}</span>
          </div>
        ),
      },
      {
        id: "protocol",
        header: "Protocol",
        accessor: (d) => d.protocol,
        cell: (d) => (
          <Badge variant="secondary" className="font-mono uppercase tracking-wide">
            {d.protocol}
          </Badge>
        ),
      },
      {
        id: "tags",
        header: "Tags",
        accessor: (d) => d.tags,
        align: "right",
        cell: (d) => <span className="font-mono">{d.tags}</span>,
      },
      {
        id: "throughput",
        header: "msg/s",
        accessor: (d) => d.msgPerSec,
        align: "right",
        cell: (d) => (
          <span className="font-mono text-on-surface-muted">
            {d.msgPerSec.toFixed(1)}
          </span>
        ),
      },
      {
        id: "status",
        header: "Status",
        accessor: (d) => d.status,
        cell: (d) => <StatusPill status={d.status} />,
      },
    ],
    [],
  );

  return (
    <PageContainer className="max-w-7xl">
      <PageHeader
        breadcrumb={
          <>
            <span>App</span>
            <span aria-hidden>/</span>
            <span className="text-on-surface">Widget catalog</span>
          </>
        }
        title="Widget catalog"
        description="Every reusable widget in apps/web/src/lib/widgets/ rendered against the foundry fixture data. Use this as the design reference until Storybook lands in Phase 2."
        actions={<Badge variant="warning">Internal · dev only</Badge>}
      />

      {/* KPI row */}
      <Section
        title="KPI"
        description="KPICard composes a label, value, optional unit, optional delta, and optional trend sparkline. The same component drives Tier 1 dashboards and Tier 2 batch summaries."
      >
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <KPICard
            label="Bath temperature"
            hint="Furnace 1 · current"
            data={{
              value: 1438,
              unit: "°C",
              trend: FX_BATH_TEMP.points.slice(-20),
              delta: {
                value: 18,
                direction: "up",
                sentiment: "neutral",
                label: "vs 15 min ago",
              },
            }}
            citation={{
              source: "tag",
              ref: "tag-furn1-bath-temp",
              derivation: "Modbus reg 40001 ÷ 10",
            }}
          />
          <KPICard
            label="Active devices"
            hint="Across all sites"
            data={{
              value: 6,
              unit: "of 8",
              delta: {
                value: 1,
                direction: "down",
                sentiment: "negative",
                label: "vs yesterday",
              },
            }}
          />
          <KPICard
            label="Messages today"
            hint="Cumulative"
            data={{
              value: 124_900,
              trend: FX_KPI_SPARK,
              delta: {
                value: 12.4,
                direction: "up",
                sentiment: "positive",
                label: "% vs yesterday",
              },
            }}
          />
          <KPICard
            label="Sand moisture"
            hint="Last reading"
            data={{
              value: 3.91,
              unit: "%",
              trend: FX_SAND_MOISTURE.points.slice(-20),
              delta: {
                value: 0.08,
                direction: "up",
                sentiment: "neutral",
                label: "vs target band",
              },
            }}
          />
        </div>

        <div className="mt-4 grid gap-3 md:grid-cols-3">
          <KPICard
            label="Loading state"
            data={{ value: 0 }}
            state={{ kind: "loading" }}
          />
          <KPICard
            label="Empty state"
            data={{ value: 0 }}
            state={{ kind: "empty" }}
          />
          <KPICard
            label="Error state"
            data={{ value: 0 }}
            state={{ kind: "error", message: "Tag not found" }}
          />
        </div>
      </Section>

      {/* Status pills */}
      <Section
        title="Status pills"
        description="Small badges expressing entity state. Used in table cells, sidebars, and dashboards."
      >
        <div className="flex flex-wrap items-center gap-2">
          <StatusPill status="online" />
          <StatusPill status="offline" />
          <StatusPill status="warning" />
          <StatusPill status="error" />
          <StatusPill status="unknown" />
          <StatusPill status="online" label="Streaming · 12 msg/s" />
        </div>
      </Section>

      {/* Sparklines */}
      <Section
        title="Sparkline"
        description="Inline mini-trend. Renders in 28–36 px height. Optional reference band + spot dot at the last point."
      >
        <div className="grid gap-3 md:grid-cols-3">
          <div className="rounded-lg border border-border p-3">
            <p className="text-xs text-on-surface-muted">Bath temperature</p>
            <SparklineCell
              points={FX_BATH_TEMP.points.slice(-30)}
              height={40}
              referenceRange={FX_BATH_TEMP.referenceRange}
            />
          </div>
          <div className="rounded-lg border border-border p-3">
            <p className="text-xs text-on-surface-muted">Power</p>
            <SparklineCell points={FX_POWER_KW.points.slice(-30)} height={40} />
          </div>
          <div className="rounded-lg border border-border p-3">
            <p className="text-xs text-on-surface-muted">Sand moisture</p>
            <SparklineCell
              points={FX_SAND_MOISTURE.points.slice(-30)}
              height={40}
              referenceRange={FX_SAND_MOISTURE.referenceRange}
            />
          </div>
        </div>
      </Section>

      {/* Timeseries */}
      <Section
        title="Timeseries"
        description="Single-series line with reference band + thresholds, plus multi-series area. Drives ~60% of dashboards."
      >
        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Bath temperature</CardTitle>
              <CardDescription>
                Reference band shaded; high/low limits dashed.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <TimeseriesChart series={[FX_BATH_TEMP]} yLabel="°C" height={260} />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Power vs moisture (area)</CardTitle>
              <CardDescription>
                Two series, area variant, legend shows by default for &gt; 1.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <TimeseriesChart
                variant="area"
                series={[FX_POWER_KW, FX_SAND_MOISTURE]}
                height={260}
              />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Two tags on different time bases</CardTitle>
              <CardDescription>
                15 minute buckets beside raw change readings: each line stays whole; the null reading breaks its own.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <TimeseriesChart series={[FX_BUCKETED, FX_CHANGES]} height={260} />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Status grid on live readings</CardTitle>
              <CardDescription>
                Two boolean state tags, an enum tag, and a bound tag that has not reported.
              </CardDescription>
            </CardHeader>
            <CardContent className="h-[220px]">
              <StatusGrid keys={Object.keys(FX_STATUS_LIVE)} liveTagData={FX_STATUS_LIVE} />
            </CardContent>
          </Card>
        </div>
        <div className="mt-4 grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Loading</CardTitle>
            </CardHeader>
            <CardContent>
              <TimeseriesChart series={[]} state={{ kind: "loading" }} height={180} />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Empty</CardTitle>
            </CardHeader>
            <CardContent>
              <TimeseriesChart series={[]} state={{ kind: "empty" }} height={180} />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Error</CardTitle>
            </CardHeader>
            <CardContent>
              <TimeseriesChart
                series={[]}
                state={{
                  kind: "error",
                  message: "Query timed out after 5s",
                  retry: () => {},
                }}
                height={180}
              />
            </CardContent>
          </Card>
        </div>
      </Section>

      {/* Table */}
      <Section
        title="Table"
        description="Generic typed table. Sortable headers, single global filter, pagination at >25 rows, row click optional."
      >
        <Table
          getRowId={(row) => row.id}
          columns={deviceColumns}
          data={FX_DEVICES}
          searchPlaceholder="Filter devices…"
          pageSize={5}
          onRowClick={(row) => console.log("clicked", row.name)}
        />
      </Section>

      {/* Event feed + Alarm list */}
      <Section
        title="Event feed & alarm list"
        description="Both consume the same EventItem base shape. Alarms add active / acknowledged state + ack action."
      >
        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader className="flex flex-row items-center gap-2">
              <Cpu className="size-4 text-on-surface-muted" />
              <CardTitle className="text-base">Recent activity</CardTitle>
            </CardHeader>
            <CardContent>
              <EventFeed events={FX_EVENTS} />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Alarms</CardTitle>
              <CardDescription>
                Active-unacked at top; ringed by severity colour. Cleared alarms
                fade out.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <AlarmList
                alarms={alarms}
                onAcknowledge={(a) =>
                  setAlarms((prev) =>
                    prev.map((x) =>
                      x.id === a.id
                        ? {
                            ...x,
                            acknowledged: true,
                            acknowledgedBy: {
                              user: "you",
                              at: new Date().toISOString(),
                            },
                          }
                        : x,
                    ),
                  )
                }
                onView={() => {}}
              />
            </CardContent>
          </Card>
        </div>
      </Section>
    </PageContainer>
  );
}

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <h2 className="text-lg font-semibold text-on-surface">{title}</h2>
        <p className="text-sm text-on-surface-muted">{description}</p>
      </div>
      {children}
    </section>
  );
}
